import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../redux";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../components/loader/loader";
import logo from "../assets/logo.png";
import loginImg from "../assets/login_img.png";
import { FiArrowLeft, FiEye, FiEyeOff, FiShield } from "react-icons/fi";

const ResetPassword = () => {
  const [showPass, setShowPass] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const email = localStorage.getItem("resetEmail");

  useEffect(() => {
    if (!email) navigate("/forgot-password");
  }, [email, navigate]);

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsMismatch = newPassword && confirmPassword && newPassword !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode || !newPassword || !confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const verifyResponse = await axios.post(`${baseUrl}/verifycode`, {
        email,
        verificationCode,
      });
      if (verifyResponse.data.message === "Verification code matched") {
        const resetResponse = await axios.post(`${baseUrl}/reset-password`, {
          email,
          verificationCode,
          newPassword,
        });
        if (resetResponse.data.message) {
          toast.success("Password reset successful");
          localStorage.removeItem("resetEmail");
          navigate("/login");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center relative px-4"
      style={{
        backgroundImage: `url(${loginImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Toaster position="top-right" />
      <Loader loading={loading} />

      {/* Full-page overlay */}
      <div className="absolute inset-0 bg-[#3d184e]/70" />

      {/* Back to login — top left */}
      <div className="absolute top-5 left-6 z-10">
        <Link
          to="/login"
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
        >
          <FiArrowLeft />
          Back to Login
        </Link>
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Card header */}
        <div className="bg-[#3d184e] px-8 py-6 flex flex-col items-center">
          <img src={logo} alt="Logo" className="h-10 w-auto mb-3" />
          <h1 className="text-white font-bold text-xl">Reset Password</h1>
          <p className="text-gray-400 text-xs mt-1 text-center">
            Enter the code sent to{" "}
            <span className="text-teal-400 font-medium">{email}</span>
          </p>
        </div>

        {/* Card body */}
        <div className="px-8 py-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-teal-100 p-4 rounded-full">
              <FiShield className="text-teal-600 text-2xl" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Verification code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Verification Code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                placeholder="Enter code from email"
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-teal-500 transition-colors tracking-widest font-mono"
              />
            </div>

            {/* New password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Enter new password"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((p) => !p)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <input
                type={showPass ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter new password"
                className={`w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors ${
                  passwordsMismatch
                    ? "border-red-400 focus:border-red-500"
                    : passwordsMatch
                    ? "border-green-400 focus:border-green-500"
                    : "border-gray-200 focus:border-teal-500"
                }`}
              />
              {passwordsMatch && (
                <p className="text-green-600 text-xs mt-1">Passwords match</p>
              )}
              {passwordsMismatch && (
                <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !!passwordsMismatch}
              className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold py-3 rounded-xl text-sm transition-colors mt-2"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <p className="text-center text-gray-400 text-xs mt-6">
            Remember your password?{" "}
            <Link to="/login" className="text-teal-600 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <p className="relative z-10 text-white/40 text-xs mt-6">
        © {new Date().getFullYear()} Revenue Pro Planner. All rights reserved.
      </p>
    </div>
  );
};

export default ResetPassword;
